/**
 * Helpers para manipular el folio del Lease.
 *
 * Port directo de `mobile-rendafin/src/features/leases-management/utils/folio.ts`
 * (M13 Iter C.3) — la lógica es framework-agnostic; se reusa tal cual en el
 * canal interno (rendafin-front-v2) para mantener una sola regla de negocio.
 *
 * Folios en producción pueden venir en dos formatos:
 *
 *  1. **Canónico** (`R-#-########-#` o más amplio `R-X-...-...`):
 *     un dígito separador entre `R-` y el resto. El dígito es editable.
 *     Equivale al "primer número del oldId" ya consolidado.
 *
 *  2. **Sin primer dígito** (`R-########-#` o similar — formato legacy
 *     de leases migrados antes del oldId): falta el primer dígito y
 *     debemos permitir agregarlo. Aquí también editamos el primer dígito,
 *     pero la operación es **insertar** uno nuevo entre `R-` y el resto.
 *
 * REGLA DEL OWNER: `lease.oldId` = EXCLUSIVAMENTE ese primer número (1–999),
 * NO el folio anterior. Históricamente los ids de arrendamiento eran números
 * 1–999; por eso se llama `oldId` y se agrega al folio como el primer grupo
 * tras `R-`. Al editar el folio, el caller persiste `oldId = el dígito nuevo`
 * (queda en sync con el folio). No guardes el folio anterior en `oldId`.
 *
 * Folios completamente fuera de estos dos patrones (ej. cadenas vacías
 * o sin prefijo `R-`) quedan como NO modificables.
 */

// El "rest" admite hexadecimal — los folios legacy migrados del Excel
// pueden incluir letras A-F (ej. `R-74086E40B-2`). Mantenemos la
// restricción del primer grupo a dígitos (regla del owner: el oldId es
// numérico) — admite **1 a 3 dígitos** porque algunos leases tienen
// oldIds más amplios.
const REST_PATTERN = '[A-Z0-9](?:[A-Z0-9-]*[A-Z0-9])?'
const CANONICAL_REGEX = new RegExp(`^(R-)(\\d{1,3})-(${REST_PATTERN})$`, 'i')
const INSERT_REGEX = new RegExp(`^(R-)(${REST_PATTERN})$`, 'i')

export const FOLIO_DIGIT_MAX_LENGTH = 3

export type FolioMode = 'edit' | 'insert'

export interface ParsedFolio {
  mode: FolioMode
  /** Prefijo fijo `R-`. */
  prefix: string
  /** Dígito editable actual; vacío en modo insert. */
  currentDigit: string
  /** Resto del folio tras el dígito editable (incluye los guiones internos). */
  rest: string
}

export function parseFolio(folio: string | null | undefined): ParsedFolio | null {
  if (!folio || typeof folio !== 'string') return null

  const canonical = folio.match(CANONICAL_REGEX)
  if (canonical && canonical[1] && canonical[2] && canonical[3]) {
    return {
      mode: 'edit',
      prefix: canonical[1],
      currentDigit: canonical[2],
      rest: canonical[3],
    }
  }

  const insertMatch = folio.match(INSERT_REGEX)
  if (insertMatch && insertMatch[1] && insertMatch[2]) {
    return {
      mode: 'insert',
      prefix: insertMatch[1],
      currentDigit: '',
      rest: insertMatch[2],
    }
  }

  return null
}

export function isFolioModifiable(folio: string | null | undefined): boolean {
  return parseFolio(folio) !== null
}

/** Construye el folio nuevo a partir del parse + el dígito final. */
export function buildFolio(parts: ParsedFolio, newDigit: string): string {
  return `${parts.prefix}${newDigit}-${parts.rest}`
}

export function isFolioDigitValid(digit: string): boolean {
  return /^\d{1,3}$/.test(digit)
}
